import nunjucks from 'nunjucks';
import { WbSafeNunjucksApi } from './WbSafeNunjucksApi';
import {
  initNunjucksEnvironmentWithLoader,
  PinnedLRUCache,
} from '@agilelab/plugin-wb-platform-common';
import XXH from 'xxhashjs';
import { NunjucksCompiler } from './NunjucksCompiler/NunjucksCompilerApi';
import { NunjucksInlineScriptCompiler } from './NunjucksCompiler/NunjucksInlineScriptCompiler';

export type InlineScriptNunjucksRendererOpts = {
  // cache size in bytes
  maxCacheSize?: number;
};

const DEFAULT_CACHE_SIZE = 1024 * 1024 * 10; // 10 MB

export const estimateSizeFn: (fn: Function) => number = fn => {
  let sizeBytes = 0;
  // rough estimate of fn memory size
  sizeBytes += fn.toString().length * 2;
  sizeBytes += 32; // estimate of key
  return sizeBytes;
};

const HASH_SEED = 987654321;
const hashFn = (str: string) => XXH.h64(str, HASH_SEED).toString(16);

/**
 * Allows to render Nunjucks templates without using unsafe-eval by precompiling them and loading them through dynamic scripting, with caching.
 * Templates must be registered before rendering.
 */
export class InlineScriptNunjucksRenderer implements WbSafeNunjucksApi {
  private env: nunjucks.Environment;
  private cache: PinnedLRUCache<string, Function>;
  private compiler: NunjucksCompiler;

  constructor(opts?: InlineScriptNunjucksRendererOpts) {
    const maxCacheSize = opts?.maxCacheSize ?? DEFAULT_CACHE_SIZE;
    const cache = new PinnedLRUCache<string, Function>(
      maxCacheSize,
      estimateSizeFn,
    );
    this.cache = cache;

    // a custom loader that returns precompiled functions taken from cache
    // follows this example from nunjucks repo: https://github.com/mozilla/nunjucks/blob/master/nunjucks/src/precompiled-loader.js
    const loader = {
      getSource(name: string) {
        const fn = cache.get(name);
        if (fn) {
          return {
            src: {
              type: 'code',
              obj: fn,
            },
            path: name,
          } as unknown as nunjucks.LoaderSource;
        }
        return null as unknown as nunjucks.LoaderSource;
      },
    };
    this.env = initNunjucksEnvironmentWithLoader(loader);
    this.compiler = new NunjucksInlineScriptCompiler();
  }

  /**
   * Loads nunjucks templates and makes them available for rendering.
   * Returns a release function to mark the templates as no longer needed and eligible for cache eviction.
   */
  load(templates: string[]) {
    const toCompile: { name: string; template: string }[] = [];
    const toUnpin: string[] = [];
    templates.forEach(tpl => {
      const tplHash = hashFn(tpl);
      // if template not in cache, add to compile list
      if (!this.cache.has(tplHash)) {
        toCompile.push({ name: tplHash, template: tpl });
      }
      // else pin the already registered template
      else {
        toUnpin.push(tplHash);
        this.cache.pin(tplHash);
      }
    });

    const compiled = this.compiler.compile(toCompile, {
      onError: tmplName => {
        // eslint-disable-next-line no-console
        console.error(
          `Failed to compile Nunjucks template: ${tmplName}. Please check if the template is valid and can be compiled successfully.`,
        );
      },
    });

    const entries: Array<[string, Function]> = compiled.map(ct => [
      ct.name,
      ct.compiledFn,
    ]);

    // add to cache newly compiled templates and pin them so they can't be evicted until released
    this.cache.batchSet(entries, { autoPin: true });

    // return a cleanup function to unpin the templates, when executed the register states they are no longer needed and can be evicted if needed
    return () => {
      toUnpin.forEach(tplHash => this.cache.unpin(tplHash));
      // also unpin newly compiled templates
      compiled.forEach(tpl => this.cache.unpin(tpl.name));
    };
  }

  /**
   * Render a previously loaded template with data.
   * If the template wasn't loaded previously or an error is thrown, returns undefined.
   */
  render(template: string, context: Record<string, any>) {
    try {
      const tplHash = hashFn(template);
      // gets hash of template to check in cache
      const compiled = this.cache.get(tplHash);
      if (!compiled) return undefined;
      return this.env.render(tplHash, context);
    } catch {
      return undefined;
    }
  }
}
